/**
 * 系統功能設定相關 API
 */

import axios from '../api/axios';
import { I18nField } from '../types';

export interface SysFunction {
  id: number;
  func_code: string;
  upper_func_id: number;
  func_name: I18nField;
  func_type: number; // 1:節點, 2:功能
  func_order: number;
  func_icon?: string;
  module_code?: string;
  module_item: string[]; // ["Create", "Read", "Update", "Delete", "Print", "File"]
  description?: string;
  is_mana: boolean;
  is_active: boolean;
  edit_by: number;
  created_at: string;
  updated_at?: string;
}

export interface SysFunctionCreate {
  func_code: string;
  upper_func_id: number;
  func_name: I18nField;
  func_type: number;
  func_order: number;
  func_icon?: string;
  module_code?: string;
  module_item: string[]; // ["Create", "Read", "Update", "Delete", "Print", "File"]
  description?: string;
  is_mana: boolean;
  is_active: boolean;
}

export interface SysFunctionUpdate {
  func_code?: string;
  upper_func_id?: number;
  func_name?: I18nField;
  func_type?: number;
  func_order?: number;
  func_icon?: string;
  module_code?: string;
  module_item?: any[];
  description?: string;
  is_mana?: boolean;
  is_active?: boolean;
}

export interface GetFunctionsParams {
  skip?: number;
  limit?: number;
  is_active?: boolean;
  func_type?: number;
  search?: string;
}

/**
 * 取得系統功能列表（平台管理專用 · require_func system_functions read）。
 * ⚠️ 僅限平台管理頁（如 SystemFunctionsPage / UserLogsPage）使用；一般使用者可達的
 * hook/util（useFunctionName / userLogHelper）請改用 getSysFunctionsFlat()（lookup 端點）。
 */
export const getSysFunctions = async (params?: GetFunctionsParams): Promise<SysFunction[]> => {
  const response = await axios.get('/api/system_functions/', { params });
  return response.data;
};

interface SysFunctionTreeNode extends SysFunction {
  children?: SysFunctionTreeNode[];
}

/**
 * 取得功能清單（扁平）· 走 /api/system_functions/tree（lookup·全登入者可讀共用參考）。
 * E6b-c：供 useFunctionName / userLogHelper 等「一般使用者可達」的程式取 func_name / id，
 * 不打平台管理專用的 GET /api/system_functions/（enforce 後會 403）。
 */
export const getSysFunctionsFlat = async (): Promise<SysFunction[]> => {
  const response = await axios.get<SysFunctionTreeNode[]>('/api/system_functions/tree');
  const out: SysFunction[] = [];
  const walk = (nodes: SysFunctionTreeNode[]) => {
    nodes.forEach((n) => {
      const { children, ...rest } = n;
      out.push(rest);
      if (children && children.length) walk(children);
    });
  };
  walk(response.data || []);
  return out;
};

/**
 * 取得系統功能資訊
 */
export const getSysFunction = async (functionId: number): Promise<SysFunction> => {
  const response = await axios.get(`/api/system_functions/${functionId}`);
  return response.data;
};

/**
 * 建立系統功能
 */
export const createSysFunction = async (data: SysFunctionCreate): Promise<SysFunction> => {
  const response = await axios.post('/api/system_functions/', data);
  return response.data;
};

/**
 * 更新系統功能
 */
export const updateSysFunction = async (functionId: number, data: SysFunctionUpdate): Promise<SysFunction> => {
  const response = await axios.put(`/api/system_functions/${functionId}`, data);
  return response.data;
};

/**
 * 刪除系統功能
 */
export const deleteSysFunction = async (functionId: number): Promise<void> => {
  await axios.delete(`/api/system_functions/${functionId}`);
};
